import React from "react";
import Image from "next/image";
import { Section } from "./Section";
import { Typography } from "./Typography";

export interface AppFeature {
  icon: string;
  iconAlt: string;
  iconWidth?: number;
  iconHeight?: number;
  title: string;
  description: string;
}

interface AppFeaturesSectionProps {
  title?: string;
  subtitle?: string;
  description?: React.ReactNode;
  features: AppFeature[];
  background?: "white" | "gray" | "red" | "gradient-red" | "gradient-light" | "transparent";
  className?: string;
}

export function AppFeaturesSection({
  title = "App Features",
  subtitle,
  description,
  features,
  background = "white",
  className = "",
}: AppFeaturesSectionProps) {
  return (
    <Section variant="default" background={background} className={className}>
      <div className="">
        {/* Header */}
        <div className="text-center mb-12">
          <Typography variant="h2" as="h2" className="mb-6">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="h3" as="h3" className="mb-4">
              {subtitle}
            </Typography>
          )}
          {description && (
            <Typography variant="body" as="div" className="text-text-secondary max-w-3xl mx-auto">
              {description}
            </Typography>
          )}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="flex flex-col gap-4 p-6 bg-white rounded-md border border-gray-200 outline-offset-[-1px] outline-gray-200 hover:shadow-md transition-shadow duration-200"
            >
              {/* Icon */}
              <div className="flex items-center justify-center">
                <Image
                  src={feature.icon}
                  alt={feature.iconAlt}
                  title={feature.iconAlt}
                  width={feature.iconWidth || 80}
                  height={feature.iconHeight || 80}
                  className="w-20 h-20 object-contain"
                />
              </div>

              {/* Title */}
              <Typography
                variant="h3"
                as="h3"
                className="text-center text-lg font-bold text-text-primary"
              >
                {feature.title}
              </Typography>

              {/* Description */}
              <Typography
                variant="body"
                as="p"
                className=" text-text-secondary text-base font-normal text-center leading-relaxed"
              >
                {feature.description}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}
